import { types, getParent, type Instance } from 'mobx-state-tree';
import State, { type IState } from './state';
import Transition, { type ITransition } from './transition';
import { uniq, flatMap } from 'lodash';
import type { IObjectType } from './object_type';
import type { IMethod } from './method';

/**
 * The FSM instance interface.
 *
 * HAND-DECLARED (not `Instance<typeof FSM>`) to anchor the recursion: FSM embeds
 * a `Transition` array whose `get fsm()` is annotated `IFSM`, so deriving this
 * from the model would loop FSM and Transition through each other's model types.
 */
export interface IFSM {
  readonly id: string;
  readonly name: string;
  readonly type: 'DEFAULT' | 'USER_DEFINED' | 'CALCULATED';
  readonly codegeneration: boolean;
  readonly states: IState[];
  readonly transitions: ITransition[];
  asString(): string;
  readonly displayName: string;
  readonly displayString: string;
  readonly objectType: IObjectType;
  getStateById(id: string): IState | undefined;
  getTransitionById(id: string): ITransition | undefined;
  readonly initialState: IState | undefined;
  readonly finalStates: IState[];
  readonly intermediaryStates: IState[];
  readonly methods: IMethod[];
  getOutgoingTransitions(source: IState): ITransition[];
  getIncomingTransitions(target: IState): ITransition[];
  findTransition(source: IState, target: IState): ITransition | undefined;
  readonly forwardInaccessibleStates: IState[];
  readonly backwardInaccessibleStates: IState[];
  findTransitionsForMethod(method: IMethod): ITransition[];
  setName(name: string): void;
  setCodeGeneration(cg: boolean): void;
  addState(id: string, name: string, type: 'INITIAL_STATE' | 'STATE' | 'FINAL_STATE'): IState;
  addTransition(
    id: string,
    name: string,
    sourceState: string,
    targetState: string,
    methods: Array<{ id: string; method?: string | null; methodName?: string | null }>
  ): ITransition;
  removeState(id: string): void;
  removeTransition(id: string): void;
}

/**
 * A MERODE FSM
 */

const FSM = types
  .model('FSM', {
    id: types.identifier,
    name: types.string,
    type: types.enumeration(['DEFAULT', 'USER_DEFINED', 'CALCULATED']),
    codegeneration: false,
    states: types.optional(types.array(State), []),
    transitions: types.optional(types.array(Transition), []),
  })
  .views((self0) => {
    const self = self0 as unknown as IFSM;
    // private helper functions
    function getBackwardInaccessibleStates(
      accessible: IState[],
      used: ITransition[],
      state: IState
    ) {
      accessible.push(state);
      for (const transition of state.incomingTransitions) {
        if (!used.includes(transition)) {
          used.push(transition);
          getBackwardInaccessibleStates(accessible, used, transition.sourceState);
        }
      }
    }

    function getForwardInaccessibleStates(
      accessible: IState[],
      used: ITransition[],
      state: IState
    ) {
      accessible.push(state);
      for (const transition of state.outgoingTransitions) {
        if (!used.includes(transition)) {
          used.push(transition);
          getForwardInaccessibleStates(accessible, used, transition.targetState);
        }
      }
    }

    // The views
    return {
      // Human-readable label, e.g. for `${fsm}` interpolation in messages.
      // (MST 7 forbids declaring a view named `toString`, so this is a plain view.)
      asString() {
        return self.displayName;
      },
      // Used in the model explorer
      get displayName() {
        return `${self.name}${self.codegeneration ? ' (selected)' : ''}`;
      },
      // Used in the logger and the report view
      get displayString() {
        return `FSM ${self.objectType.name}.${self.name}`;
      },
      // Convenience method to get the object type
      get objectType(): IObjectType {
        // up 2-levels because 1 level up is the array of fsms.
        return getParent(self0, 2);
      },
      // get a state by id
      getStateById(id: string) {
        return self.states.find((s) => s.id === id);
      },
      // get a transition by id
      getTransitionById(id: string) {
        return self.transitions.find((t) => t.id === id);
      },
      // get the initial state
      get initialState() {
        return self.states.find((s) => s.type === 'INITIAL_STATE');
      },
      // get the final states
      get finalStates() {
        return self.states.filter((s) => s.type === 'FINAL_STATE');
      },
      // get all intermediary states
      get intermediaryStates() {
        return self.states.filter((s) => s.type === 'STATE');
      },
      // get the methods of all transitions
      get methods() {
        return uniq(flatMap(self.transitions, (t) => t.metaMethods.slice()));
      },
      // get the outgoing transitions for the given state
      getOutgoingTransitions(source: IState) {
        return self.transitions.filter((t) => t.sourceState === source);
      },
      // get the incoming transitions for the given state
      getIncomingTransitions(target: IState) {
        return self.transitions.filter((t) => t.targetState === target);
      },
      // find the transition between the two states given
      findTransition(source: IState, target: IState) {
        return self.transitions.find((t) => t.sourceState === source && t.targetState === target);
      },
      // get the forward inaccessible states
      get forwardInaccessibleStates() {
        if (self.initialState != null) {
          const accessible: IState[] = [];
          getForwardInaccessibleStates(accessible, [], self.initialState);
          return self.states.filter((s) => !accessible.includes(s));
        } else {
          // No initial state => all states are forward inaccessible;
          return self.states.slice();
        }
      },
      // get the backward inaccessible states
      get backwardInaccessibleStates() {
        const accessible: IState[] = [];
        for (const state of self.finalStates) {
          getBackwardInaccessibleStates(accessible, [], state);
        }
        return self.states.filter((s) => !accessible.includes(s));
      },
      findTransitionsForMethod(method: IMethod) {
        return self.transitions.filter((t) => t.hasMethod(method));
      },
    };
  })
  .actions((self) => ({
    setName(name: string) {
      self.name = name;
    },
    setCodeGeneration(cg: boolean) {
      self.codegeneration = cg;
    },
    addState(id: string, name: string, type: 'INITIAL_STATE' | 'STATE' | 'FINAL_STATE') {
      const state = State.create({
        id,
        name,
        type,
      });
      self.states.push(state);
      return state;
    },
    addTransition(
      id: string,
      name: string,
      sourceState: string,
      targetState: string,
      methods: Array<{ id: string; method?: string | null; methodName?: string | null }>
    ) {
      const transition = Transition.create({
        id,
        name,
        sourceState,
        targetState,
        methods,
      });
      self.transitions.push(transition);
      return transition;
    },
    // id? To be able to replay, we need to have serializable arguments!
    removeState(id: string) {
      const state = self.getStateById(id);
      // MobX observable array has a 'remove' method!
      self.states.remove(state as unknown as Instance<typeof State>);
    },
    // id? To be able to replay, we need to have serializable arguments!
    removeTransition(id: string) {
      const transition = self.getTransitionById(id);
      // MobX observable array has a 'remove' method!
      self.transitions.remove(transition as unknown as Instance<typeof Transition>);
    },
  }));

export default FSM;
